import React, { forwardRef, useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import InputMask from "react-input-mask";
import { get, chain, isString } from "lodash";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import FieldIcon from "./FieldIcon";

const _ = { get, chain, isString };

const isFilledDate = (value: unknown) =>
  Boolean(value) && String(value).trim() !== "";

// We will explicity destruct props which are not Mui props or want to manipulates
// "...props" will have all the props which is accepted by Mui component, e.g. variant, onChange etc...
interface InputProps {
  // Primary props
  name: string;
  className?: string;
  type?: "text" | "number" | "email" | "password" | "file" | "date";
  variant?: "filled" | "outlined" | "standard";
  size?: "small" | "medium";
  isPassword?: true | false; // Whether input should show/hide password characters
  fullWidth?: true | false;
  multiline?: true | false;
  inputRef?: any;
  mask?: string;
  showCount?: boolean;
  maxLimit?: number;
  countValue?: number;
  counterAlign?: "left" | "center" | "right";
  // Validation handling (NOTE: only supports react-hook-form for the moment)
  validationObj?: object;
  externalLabel?: externalLabel;
  icon?: React.ReactNode;
  inputIcon?: any;
  // other props of the component which directly used in component
  [key: string]: any;
}

interface externalLabel {
  [key: string]: any;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  validationObj,
  externalLabel,
  mask,
  inputRef,
  isPassword,
  type,
  showCount,
  maxLimit = 1000,
  counterAlign = "right",
  countValue,
  icon,
  inputIcon,
  ...props
},ref) => {
  const leadingIcon = icon || inputIcon;
  const [inputType, setInputType] = useState(type);
  const [showPassword, setShowPassword] = useState(false);
  const [hasDateValue, setHasDateValue] = useState(
    isFilledDate(props.value ?? props.defaultValue)
  );

  useEffect(() => {
    if (type === "date" && props.value !== undefined) {
      setHasDateValue(isFilledDate(props.value));
    }
  }, [type, props.value]);

  let inputRefClass =
    _.get(inputRef, "name", "") === "" && inputRef ? "required" : "";
  const isRequired = _.get(props, "required", "");
  if (isRequired === false) {
    inputRefClass = "";
  }
  //  External label
  const _externalLabel = _.get(externalLabel, "label", "") || "";
  let externalLabelClass = _.get(externalLabel, "className", "") || "";
  const externalLabelClasses = _.chain([inputRefClass, externalLabelClass])
    .uniq()
    .join(" ")
    .split(" ")
    .uniq()
    .join(" ")
    .trim()
    .value();
  const inputMask = _.isString(mask) ? mask : "";

  const _renderPasswordIcon = () => {
    if (type === "password") {
      return {
        endAdornment: showPassword ? (
          <Visibility
            className="password-eye-icon"
            onClick={() => {
              setShowPassword(false);
              setInputType("password");
            }}
          />
        ) : (
          <VisibilityOff
            className="password-eye-icon"
            onClick={() => {
              setShowPassword(true);
              setInputType("text");
            }}
          />
        ),
      };
    } else {
      return {};
    }
  };

  const fieldName = props.name || "";
  const fieldError = _.get(validationObj, fieldName) as
    | { message?: string; type?: string }
    | undefined;
  const errorMessage = fieldError?.message ?? null;

  const openDatePicker = (input?: HTMLInputElement | null) => {
    if (!input || input.disabled || typeof input.showPicker !== "function") {
      return;
    }
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  };

  const renderInput = (fieldProps: any) => {
    const dateStateClass =
      type === "date"
        ? hasDateValue
          ? "has-date-value"
          : "is-date-empty"
        : "";
    const isDateField = type === "date";
    const incomingStart = fieldProps.InputProps?.startAdornment;
    const startIcon =
      leadingIcon ||
      incomingStart?.props?.children ||
      incomingStart ||
      null;

    return (
      <>
        <TextField
          // To override these property pass it in props of controller

          // Regular props
          {...fieldProps}
          className={[fieldProps.className, isDateField && "eh-date-field"]
            .filter(Boolean)
            .join(" ")}
          inputProps={{
            ...fieldProps.inputProps,
            className: [fieldProps.inputProps?.className, dateStateClass]
              .filter(Boolean)
              .join(" "),
          }}
          InputProps={{
            ...fieldProps.InputProps,
            ..._renderPasswordIcon(),
            ...(startIcon && {
              startAdornment: <FieldIcon>{startIcon}</FieldIcon>,
            }),
            ...(isDateField && {
              onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                fieldProps.InputProps?.onClick?.(e);
                if (fieldProps.disabled) {
                  return;
                }
                openDatePicker(e.currentTarget.querySelector("input"));
              },
            }),
          }}
          id={fieldProps.name}
          type={inputType}
          inputRef={inputRef || ref}
          error={Boolean(errorMessage || fieldError?.type)}
          helperText={errorMessage}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (isDateField) {
              setHasDateValue(isFilledDate(e.target.value));
            }
            fieldProps.onChange?.(e);
          }}
        />
        
        {showCount && (
          <p
            className={` mt-2 global-counter
            ${
              (counterAlign === "center" || counterAlign === "right") &&
              `align-${counterAlign}`
            }  ${(countValue || 0) > maxLimit && "error-text"}`}
          >
            {`${countValue || 0}/${maxLimit}`}
          </p>
        )}
      </>
    );
  };

  return (
    <>
      {_externalLabel && (
        <label htmlFor={props.name} className={externalLabelClasses}>
          {_externalLabel}
        </label>
      )}
      {inputMask !== "" ? (
        // @ts-ignore
        <InputMask mask={inputMask} {...props}>
          {/* @ts-ignore */}
          {(inputProps: any) => renderInput({ ...inputProps, ref })}
        </InputMask>
      ) : (
        renderInput({ ...props, ref })
      )}
    </>
  );
});

// Default props of the component
Input.defaultProps = {
  type: "text",
  variant: "outlined",
  size: "small",
  fullWidth: true,
};

export default Input;
